<template>
<div class="list-analytics timeline-chart">
  <div class="t-chart">
    <div class="title">이벤트 타임라인</div>
    <div>
      <highcharts class="chart c-pointer" style="margin-top:3px;" :options="options" />
      <div class="status">
        <ul class="list list-event">
          <li :style="{borderColor: (legendPoint ? legendPoint.color : null)}">
            <span class="number">{{ getEventCount(legendPoint) }} </span>
            <span class="content">
              <span>이벤트 발생 건 수</span>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <w-event-list-popup :title="popupTitle" ref="eventListPopup"></w-event-list-popup>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import MsgUtil from '@/utils/message';
import findLast from 'lodash/findLast';
import moment from 'moment';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import remove from 'lodash/remove';

import { objType } from '@/constants/obj';
import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import ChartAPI from '@/api/performance/analysis/analysis-chart';
import WEventListPopup from './popup/EventListPopup';

const options = {
  time: {
    useUTC: false,
  },
  chart: {
    type: 'line',
    height: 30,
    backgroundColor: 'transparent',
  },
  title: false,
  credits: false,
  xAxis: {
    visible: false,
    type: 'datetime',
    labels: {
      format: '{value: %Y-%m-%d %H:%M}',
      style: {
        color: '$chinagray',
        fontSize: '9px',
      },
    },
    lineWidth: 0,
  },
  yAxis: {
    labels: false,
    title: false,
    tickInterval: 0,
    gridLineColor: '#b0aeae',
  },
  plotOptions: {
    line: {
      animation: false,
      lineWidth: 0,
      states: {
        hover: {
          lineWidthPlus: 0,
        },
      },
      marker: {
        enabled: true,
        radius: 4.5,
        lineWidth: 1,
        lineColor: 'rgba(255, 255, 255, 0.5)',
      },
      dataLabels: {
        formatter() {
          return '<i class="el-icon-caret-bottom" style="font-size: 20px; color:#2a3346;"/>';
        },
        verticalAlign: 'middle',
        useHTML: true,
      },
    },
  },
  legend: false,
  tooltip: {
    padding: 5,
    outside: true,
    useHTML: true,
    formatter() {
      return `${getDateToString(this.x, 'YYYY-MM-DD HH:mm:ss')}: ${this.point.count}건`;
    },
  },
  series: [],
};

export default {
  name: 'WTimelineChart',
  props: {
    active: Boolean,
  },
  components: {
    WEventListPopup,
  },
  data() {
    return {
      loading: false,
      isDoubleClick: 0,
      popupTitle: '',
      options: cloneDeep(options),
      legendPoint: {},
      beforePoint: null,
    };
  },
  computed: mapGetters(['selectedNode', 'analysisDateParams', 'isRelativeAnalysisDate']),
  watch: {
    active(active) {
      if (active) {
        this.onShowChart();
      }
    },
    selectedNode: {
      handler() {
        if (!this.active) {
          return;
        }
        this.onShowChart();
      },
    },
    analysisDateParams() {
      if (!this.active) {
        return;
      }
      this.onShowChart();
    },
  },
  /**
   * 타임라인 차트의 이벤트를 등록한다.
   * @return {[type]} [description]
   * @author jda
   */
  mounted() {
    const me = this;
    this.options.chart.events = {
      click(e) {
        me.setTimeLineEvent(this, e);
      },
      load() {
        if (!isEmpty(this.series)) {
          this.series[0].update({
            events: {
              click(e) {
                me.isDoubleClick += 1;
                setTimeout(() => {
                  if (me.isDoubleClick === 1) {
                    me.setTimeLineEvent(this.chart, e);
                  } else if (me.isDoubleClick > 1) {
                    me.showEventList(this.chart, e);
                  }
                  me.isDoubleClick = 0;
                }, 400);
              },
            },
            point: {
              events: {
                mouseOver() {
                  me.legendPoint = this;
                },
              },
            },
          });
          me.legendPoint = findLast(this.series[0].points, (point) => point.y === 0);
        }
      },
    };
  },
  methods: {
    showEventList(chart, e) {
      if (chart.series.length < 1) {
        return;
      }

      const point = chart.series[0].searchPoint(e, true);
      if (isNil(point)) {
        return;
      }
      this.dialogTableVisible = true;
      this.popupTitle = `이벤트 리스트 (${this.getDateFormatter(null, null, point.x)})`;
      this.$refs.eventListPopup.open(point.x);
    },
    getDateFormatter(row, column, value) {
      return isNil(value) ? EMPTY_TEXT : moment(new Date(value)).format('YYYY-MM-DD HH:mm:ss');
    },
    /**
     * 이벤트 타임라인에서 선택한 데이터를 표현한다.
     * @param  {[type]} chart [description]
     * @param  {[type]} e     [description]
     * @author jda
     */
    setTimeLineEvent(chart, e) {
      this.$emit('clickChart', chart, e);

      const series = chart.series[0];
      if (series) {
        const point = series.searchPoint(e, true);
        if (point) {
          if (this.beforePoint) {
            this.beforePoint.update({
              dataLabels: {
                enabled: false,
              },
            });
          }

          point.update({
            dataLabels: {
              enabled: true,
            },
          });
          this.legendPoint = point;
          this.beforePoint = point;
        }
      }
    },
    /**
     * 이벤트 타임라인 데이터를 가져온다.
     * @return {[type]} [description]
     * @author jda
     */
    onShowChart() {
      this.point = null;
      remove(this.legendPoint);
      const selectedNode = this.selectedNode;
      if (selectedNode.type !== 'obj') {
        return;
      }

      // e2e는 rsc_key를 보내 서비스에서 obj_id를 찾는다.
      const params = Object.assign({}, this.analysisDateParams, { isRelative: this.isRelativeAnalysisDate });
      if (selectedNode.subType === objType.E2E) {
        Object.assign(params, {
          rscKey: selectedNode.rscKey,
        });
      } else {
        Object.assign(params, {
          objId: selectedNode.obj.objId,
        });
      }
      this.loading = true;
      ChartAPI.getPerformanceEventTimelineData(params).then((response) => {
        if (response.data.success) {
          this.options.series = [
            {
              name: '이벤트 발생 건 수',
              data: response.data.data,
            },
          ];
          this.beforePoint = null;
        } else {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 타임라인 정보', false));
        }
      }).catch((e) => {
        this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 타임라인 정보', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    /**
     * 이벤트 발생 건 수를 표현한다.
     * @param  {[type]} legendPoint [description]
     * @return {[type]}             [description]
     * @author jda
     */
    getEventCount(legendPoint) {
      if (isEmpty(legendPoint)) {
        return EMPTY_TEXT;
      }

      return `${legendPoint.options.count} 건`;
    },
  },
};
</script>

<style lang="scss">
.list-analytics.timeline-chart {
  $timeline-height: 30px;
  height: $timeline-height;
  & > .t-chart {
    min-height: $timeline-height;
    background: #f4f5f6;
    .title {
      background: #3665b3;
      color: #fff;
    }
    .chart {
      height: auto;
      margin-top: 5px;
    }
    .status {
      height: 21px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/components/TimelineChart.vue